import React from "react";
import "./LayerModal.css";
import Sortable from "sortablejs";
import { useTranslation, translate } from "../utils/languageUtils";
import { BiLayer, BiX, BiLock, BiLockOpen, BiTrash, BiChevronUp, BiChevronDown } from 'react-icons/bi';

class LayerModal extends React.Component {
  constructor(props) {
    super(props);
    this.listRef = React.createRef();
    this.state = {
      items: []
    };
  }
  
  componentDidMount() {
    // Only initialize if modal is open
    if (this.props.isOpen) {
      // Load layers from controller
      this.loadDesignGroupFromController();
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => this.initializeSortable(), 0);
    }
  }
  
  componentDidUpdate(prevProps) {
    // If the modal was closed and now is open, reinitialize sortable
    if (!prevProps.isOpen && this.props.isOpen) {
      // Load layers from controller
      this.loadDesignGroupFromController();
      setTimeout(() => this.initializeSortable(), 0);
    }
  }
  
  componentWillUnmount() {
    // Safely destroy sortable if it exists
    try {
      if (this.sortable) {
        this.sortable.destroy();
      }
    } catch (error) {
      console.log('Error destroying sortable:', error);
    }
  }
  
  // Load layers from the controller
  loadDesignGroupFromController() {
    //1.load the design group from the controller
    const nodes = this.props.controller.design_group.children;
    //alert(nodes.length);
    const items=[];
    for (let i = 0; i < nodes.length; i++) {
      const nodeName = nodes[i].name() === "" ? "NA" : nodes[i].name();
      if (nodeName === 'hint-layer') {
        continue;
      }
      // Special base layers: product, background (image/color), and case
      const isSpecialNode = (
        nodeName === 'product-image' ||
        nodeName === 'background-image' ||
        nodeName === 'background-color' ||
        nodeName === 'case-image' ||
        nodeName === 'hint-layer' ||
        (nodeName && (
          nodeName.includes('product') ||
          nodeName.includes('background') ||
          nodeName.includes('case') ||
          nodeName.includes('hint')
        ))
      );
      
      // If it's a product or background, lock the node in the UI
      if (isSpecialNode) {
        nodes[i].draggable(false); // Lock the node
        try { nodes[i].listening(false); } catch (_) {}
      }
      
      items.push({
        id: nodes[i].id(),
        name: nodeName,
        type: nodes[i].getClassName(),
        object: nodes[i],
        zIndex: nodes[i].zIndex(),
        locked: nodes[i].draggable() === false,
        isSpecial: !!isSpecialNode
      });
    }

    // Sort: regular layers first by z-index desc, then special layers at the bottom by z-index desc
    items.sort((a, b) => {
      if (a.isSpecial !== b.isSpecial) {
        return a.isSpecial ? 1 : -1; // push specials to bottom
      }
      return b.zIndex - a.zIndex;
    });

    this.setState({
      items: items
    });
  }
  
  // Helper to determine layer type
  getLayerType(node) {
    if (!node) return 'unknown';
    
    if (node.getClassName() === 'Text') return 'text';
    if (node.getClassName() === 'Image') {
      if (node.name() === 'background-image') return 'background';
      return 'image';
    }
    return node.getClassName().toLowerCase();
  }
  
  // Generate preview content for each layer type
  getPreviewContent(item) {
    if (!item || !item.object) return null;
    
    const obj = item.object;
    const isKonvaObject = obj.attrs && typeof obj.getAttr === 'function';
    
    if (!isKonvaObject) {
      return <div className="layer-preview">⚪</div>;
    }
    
    switch (item.type.toLowerCase()) {
      case 'text':
        // Show the text content with styling
        try {
          const textContent = obj.text() || '';
          const fontColor = obj.fill() || '#333';
          const fontFamily = obj.fontFamily() || 'Arial, sans-serif';
          
          return (
            <div className="layer-preview image-preview">
              <span style={{ 
                fontSize: '11px', 
                color: fontColor,
                fontFamily: fontFamily,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
                padding: '0 3px'
              }}>
                {textContent}
              </span>
            </div>
          );
        } catch (error) {
          console.error('Error creating text preview:', error);
          return <div className="layer-preview">Aa</div>;
        }
        
      case 'image':
        // Show a small thumbnail of the image
        try {
          const imageElement = obj.image();
          
          if (imageElement && imageElement.src) {
            return (
              <div className="layer-preview image-preview">
                <img 
                  src={imageElement.src} 
                  alt={item.name || 'Image'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              </div>
            );
          } else {
            // Fallback if no image source is available
            return (
              <div className="layer-preview image-preview">
                <div className="image-icon">🖼️</div>
              </div>
            );
          }
        } catch (error) {
          console.error('Error creating image preview:', error);
          return <div className="layer-preview">🖼️</div>;
        }
        
      case 'group':
        return <div className="layer-preview">📑</div>;
        
      case 'rect':
      case 'circle':
      case 'path':
        // Show a colored shape based on the fill color
        try {
          const fill = obj.fill() || '#cccccc';
          const stroke = obj.stroke() || 'transparent';
          const strokeWidth = obj.strokeWidth() || 0;
          
          return (
            <div className="layer-preview shape-preview">
              <div className="shape" style={{
                backgroundColor: fill,
                border: stroke !== 'transparent' ? `${strokeWidth}px solid ${stroke}` : 'none',
                borderRadius: item.type.toLowerCase() === 'circle' ? '50%' : '2px',
                width: '20px',
                height: '20px'
              }}></div>
            </div>
          );
        } catch (error) {
          console.error('Error creating shape preview:', error);
          return <div className="layer-preview">⬜</div>;
        }
        
      default:
        return <div className="layer-preview">⚪</div>;
    }
  }
  
  initializeSortable() {
    // Safety check - only proceed if modal is open
    if (!this.props.isOpen) return;
    
    const list = this.listRef.current;
    
    if (list) {
      // Safely destroy existing sortable instance if it exists
      try {
        if (this.sortable) {
          this.sortable.destroy();
        }
      } catch (error) {
        console.log('Error destroying existing sortable:', error);
      }
      
      // Create new sortable instance
      this.sortable = new Sortable(list, {
        animation: 120,
        ghostClass: 'dragging', // Class for the dragged item
        filter: '.locked, .layer-actions button', // Prevent dragging of locked items and when clicking action buttons
        preventOnFilter: true, // Prevent default when trying to drag filtered items
        forceFallback: true,
        fallbackTolerance: 6,
        scroll: true,
        bubbleScroll: true,
        store: {
          set: (sortable) => {
            // Get the new order of items
            const order = sortable.toArray();
            const allItems = this.state.items.slice();
            const unlocked = allItems.filter(it => !it.locked && !it.isSpecial);
            const lockedOrSpecial = allItems.filter(it => it.locked || it.isSpecial);
            const orderedUnlocked = order
              .map(id => unlocked.find(item => item.id.toString() === id))
              .filter(Boolean);
            const merged = [...orderedUnlocked, ...lockedOrSpecial];
            this.setState({ items: merged }, () => {
              this.updateZIndexes();
            });
          }
        },
        dataIdAttr: 'data-id', // Attribute to use for item IDs
        onStart: (evt) => {
          // Check if the item is locked
          const itemEl = evt.item;
          if (itemEl.classList.contains('locked')) {
            // Cancel the drag operation
            evt.preventDefault();
            return false;
          }
        }
      });
    }
  }
  
  // Update z-indexes in the controller based on the current order
  updateZIndexes() {
    const { controller } = this.props;
    if (!controller) return;
    controller.updateLayerOrder(this.state.items);
    
    console.log('Updated z-indexes manually');
  }
  
  // Move item up among unlocked items
  moveItemUp = (item) => {
    const items = this.state.items.slice();
    const idx = items.findIndex(i => i.id === item.id);
    if (idx <= 0) return;
    // Only swap with previous non-special, non-locked
    for (let j = idx - 1; j >= 0; j--) {
      const prev = items[j];
      if (!prev.locked && !prev.isSpecial) {
        [items[j], items[idx]] = [items[idx], items[j]];
        break;
      }
    }
    this.setState({ items }, () => this.updateZIndexes());
  }
  
  // Move item down among unlocked items
  moveItemDown = (item) => {
    const items = this.state.items.slice();
    const idx = items.findIndex(i => i.id === item.id);
    if (idx < 0 || idx >= items.length - 1) return;
    for (let j = idx + 1; j < items.length; j++) {
      const next = items[j];
      if (!next.locked && !next.isSpecial) {
        [items[j], items[idx]] = [items[idx], items[j]];
        break;
      }
    }
    this.setState({ items }, () => this.updateZIndexes());
  }
  
  // Handle layer lock/unlock
  handleToggleLock(item) {
    const { controller } = this.props;
    if (!controller || !item.object) return;
    // Prevent operations on special base layers
    if (item.isSpecial) return;
    
    // Toggle locked state
    const newLockedState = !item.locked;
    const obj = item.object;
    
    // Update Konva object properties if it's a Konva object
    if (obj.attrs && typeof obj.draggable === 'function') {
      obj.draggable(!newLockedState);
      obj.listening(!newLockedState); // Set listening to false when locked
      console.log(`Layer ${item.name} - draggable: ${!newLockedState}, listening: ${!newLockedState}`);
    }
    
    // Update item in state with new lock state
    this.setState(prevState => ({
      items: prevState.items.map(prevItem => 
        prevItem.id === item.id 
          ? { ...prevItem, locked: newLockedState } 
          : prevItem
      )
    }));
    
    // Force redraw to apply changes
    if (controller.redraw) {
      controller.redraw();
    } else if (controller.stage && controller.stage.batchDraw) {
      controller.stage.batchDraw();
    }
  }
  
  // Handle layer delete
  handleDeleteLayer(item) {
    const { controller } = this.props;
    if (!controller || !item.object) return;
    // Prevent operations on special base layers
    if (item.isSpecial) return;
    
    const obj = item.object;
    
    // Remove Konva object if it's a Konva object
    if (obj.attrs && typeof obj.destroy === 'function') {
      obj.destroy();
      
      // Update stage
      if (controller.redraw) {
        controller.redraw();
      } else if (controller.stage && controller.stage.batchDraw) {
        controller.stage.batchDraw();
      }
    }
    
    // Remove item from state
    this.setState(prevState => ({
      items: prevState.items.filter(prevItem => prevItem.id !== item.id)
    }));
  }
  
  // Render a layer item with preview, name, and actions
  renderLayerItem(item, index) {
    const isSpecial = !!item.isSpecial;
    return (
      <li 
        key={`${item.id}-${index}`} 
        className={`layer-item ${item.locked ? 'locked' : ''} ${isSpecial ? 'special' : ''}`}
        data-id={item.id}
      >
        {(item.locked || isSpecial) ? (
          <div className="drag-handle locked-handle" style={{ cursor: 'not-allowed' }}>⋮⋮</div>
        ) : (
          <div className="drag-handle">⋮⋮</div>
        )}
        {this.getPreviewContent(item)}
        <div className="layer-info">
          <div className="layer-name">{item.name}</div>
          <div className="layer-type">{item.type}</div>
        </div>
        <div className="right-controls">
          <div className="layer-index">{translate('layer.zIndex')}{item.zIndex}</div>
          {!isSpecial && (
            <div className="layer-actions">
              <button
                className="move-button"
                onClick={() => this.moveItemUp(item)}
                title={translate('layer.moveUp')}
              >
                <BiChevronUp size={16} />
              </button>
              <button
                className="move-button"
                onClick={() => this.moveItemDown(item)}
                title={translate('layer.moveDown')}
              >
                <BiChevronDown size={16} />
              </button>
              <button 
                className={`lock-button ${item.locked ? 'locked' : ''}`}
                onClick={() => this.handleToggleLock(item)}
                title={item.locked ? translate('layer.unlockLayer') : translate('layer.lockLayer')}
              >
                {item.locked ? <BiLock size={16} /> : <BiLockOpen size={16} />}
              </button>
              <button 
                className="delete-button"
                onClick={() => this.handleDeleteLayer(item)}
                title={translate('layer.deleteLayer')}
              >
                <BiTrash size={16} />
              </button>
            </div>
          )}
        </div>
      </li>
    );
  }
  
  render() {
    const { isOpen, onClose } = this.props;
    const { items } = this.state;
    
    if (!isOpen) return null;
    
    return (
      <div className="layer-modal-overlay" onClick={onClose}>
        <div className="layer-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="layer-modal-header">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <BiLayer size={18} /> {translate('layer.title')}
            </h3>
            <button className="close-button" onClick={onClose} aria-label="Close">
              <BiX size={18} />
            </button>
          </div>
          
          <div className="layer-modal-body">
            {items.length === 0 ? (
              <div className="no-layers-message">{translate('layer.noLayers')}</div>
            ) : (
              <ul className="layer-list" ref={this.listRef}>
                {items.map((item, index) => this.renderLayerItem(item, index))}
              </ul>
            )}
          </div>
          
          {/** Footer removed to avoid duplicated close button. Top-right BiX remains. **/}
        </div>
      </div>
    );
  }
}

export default LayerModal;
